import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { t } from "../i18n";
import "../Component/Profile.css";
import "../styles/Themes.css";
import ProfileIcon from "../assets/user.png";
import gamesData from "./gamesData";
import { useUser } from "../context/UserAttrContext";

// Badge icons
import firstPost from "../BadgesIcons/firstPost.png";
import Bio from "../BadgesIcons/Bio.png";
import AccountCreated from "../BadgesIcons/AccountCreated.png";
import discus from "../BadgesIcons/online-discussion.png"; // Engagement
import favgame from "../BadgesIcons/like.png";
import Friend from "../BadgesIcons/Friends.png";
import { DEFAULT_AVATAR } from "../utils/avatar";

// Default profile image
const DEFAULT_PROFILE_ICON = DEFAULT_AVATAR;

/* -------------------- Helper Functions -------------------- */
function formatTimeAgo(createdAt) {
if (!createdAt) return "Unknown date";
const date = new Date(createdAt);
const now = new Date();
const diffMs = now - date;
const diffMins = Math.round(diffMs / 60000);
if (diffMins < 60)
return `${diffMins} minute${diffMins !== 1 ? "s" : ""} ago`;
const diffHours = Math.round(diffMins / 60);
if (diffHours < 24)
return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
const diffDays = Math.round(diffHours / 24);
return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
}

function getTierClasses(tierName) {
switch (tierName) {
case "Rookie":
return { frameClass: "badge-frame-rookie", fillClass: "fill-rookie" };
case "Bronze":
return { frameClass: "badge-frame-bronze", fillClass: "fill-bronze" };
case "Silver":
return { frameClass: "badge-frame-silver", fillClass: "fill-silver" };
case "Gold":
return { frameClass: "badge-frame-gold", fillClass: "fill-gold" };
case "Platinum":
return { frameClass: "badge-frame-platinum", fillClass: "fill-platinum" };
default:
return { frameClass: "badge-frame-rookie", fillClass: "fill-rookie" };
}
}

/* -------------------- Profile Component -------------------- */
const Profile = () => {
const navigate = useNavigate();
const { userId: paramUserId } = useParams();
const loggedInUserId = sessionStorage.getItem("user");
const effectiveUserId = paramUserId || loggedInUserId;
const isOwnProfile = !paramUserId || paramUserId === loggedInUserId;
const token = sessionStorage.getItem("token");

// Dark mode toggle
// User Info from context or fetched public profile
const { user, updateAttributes } = useUser();
const [publicProfile, setPublicProfile] = useState(null);
const profileData = isOwnProfile ? user : publicProfile;

// Bio editing state
const [editedBio, setEditedBio] = useState("");
const [editing, setEditing] = useState(false);
const [activeTab, setActiveTab] = useState("overview");

// Posts & loading state
const [allPosts, setAllPosts] = useState([]);
const [loading, setLoading] = useState(false);

// Friends state
const [friends, setFriends] = useState([]);
const [friendsLoading, setFriendsLoading] = useState(false);
const [friendsError, setFriendsError] = useState(null);

// Friend request state
const [connectionStatus, setConnectionStatus] = useState(null);
const [connectionId, setConnectionId] = useState(null);
const [connectionLoading, setConnectionLoading] = useState(false);

// Badge popups and modal state
const [openEarnedPopupIndex, setOpenEarnedPopupIndex] = useState(null);
const [openInProgressPopupIndex, setOpenInProgressPopupIndex] = useState(null);
const [showBadgeModal, setShowBadgeModal] = useState(false);

/* -------------------- Data Fetching -------------------- */
// Fetch Friends
useEffect(() => {
if (!token || !effectiveUserId) return;
setFriendsLoading(true);
fetch(`${process.env.REACT_APP_API_PATH}/connections?anyUserID=${effectiveUserId}`, {
headers: {
  "Content-Type": "application/json",
  Authorization: `Bearer ${token}`,
},
})
.then((res) => res.json())
.then((result) => {
  if (result && result[0]) {
    const friendsList = result[0]
      .filter((conn) => conn.attributes.status === "active")
      .map((conn) => {
        const friend =
          conn.fromUser.id === parseInt(effectiveUserId, 10)
            ? conn.toUser
            : conn.fromUser;
        return {
          id: friend.id,
          name: friend.attributes?.username || "",
          img: friend.attributes?.picture || ProfileIcon,
        };
      });
    setFriends(friendsList);
  } else {
    setFriends([]);
  }
  setFriendsLoading(false);
})
.catch((err) => {
  console.error("Error fetching friends:", err);
  setFriendsError(err.message);
  setFriendsLoading(false);
});
}, [effectiveUserId, token]);

// Fetch public profile if needed
useEffect(() => {
if (!isOwnProfile && effectiveUserId) {
fetch(`${process.env.REACT_APP_API_PATH}/users/${effectiveUserId}`, {
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  },
})
  .then((res) => res.json())
  .then((data) => setPublicProfile(data.attributes || data))
  .catch((err) => console.error("Error fetching public profile:", err));
}
}, [effectiveUserId, isOwnProfile, token]);

// Fetch connection status for friend request
useEffect(() => {
if (isOwnProfile || !token || !loggedInUserId || !effectiveUserId) return;
fetch(`${process.env.REACT_APP_API_PATH}/connections?anyUserID=${loggedInUserId}`, {
  headers: {
    Authorization: `Bearer ${token}`,
  },
})
.then((res) => res.json())
.then((result) => {
  if (result && result[0]) {
    const connection = result[0].find((conn) => {
      return (
        (conn.fromUser.id === parseInt(loggedInUserId) && conn.toUser.id === parseInt(effectiveUserId)) ||
        (conn.fromUser.id === parseInt(effectiveUserId) && conn.toUser.id === parseInt(loggedInUserId))
      );
    });
    if (connection) {
      setConnectionStatus(connection.attributes.status);
      setConnectionId(connection.id);
    } else {
      setConnectionStatus(null);
      setConnectionId(null);
    }
  }
})
.catch((err) => {
  console.error("Error fetching connection status:", err);
});
}, [isOwnProfile, token, loggedInUserId, effectiveUserId]);

// Update bio from profileData once loaded
useEffect(() => {
if (profileData && !editing) {
setEditedBio(profileData.bio || "");
}
}, [profileData, editing]);

// Fetch posts
useEffect(() => {
if (!token) return;
setLoading(true);
fetch(`${process.env.REACT_APP_API_PATH}/posts?limit=9999`, {
headers: {
  "Content-Type": "application/json",
  Authorization: `Bearer ${token}`,
},
})
.then((res) => res.json())
.then((data) => {
  const posts = (data && data[0]) || [];
  setAllPosts(posts);
  setLoading(false);
})
.catch((err) => {
  console.error("Error fetching posts:", err);
  setLoading(false);
});
}, [token]);

// Handle friend request
const handleFriendRequest = async () => {
if (connectionLoading || !token || !loggedInUserId || !effectiveUserId) return;
setConnectionLoading(true);

if (connectionStatus === "pending") {
  // Cancel the pending request
  fetch(`${process.env.REACT_APP_API_PATH}/connections/${connectionId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      attributes: { status: "canceled", type: "friend" },
    }),
  })
  .then((res) => res.json())
  .then(() => {
    setConnectionStatus("canceled");
    setConnectionId(null);
  })
  .catch((err) => {
    console.error("Error canceling friend request:", err);
  })
  .finally(() => setConnectionLoading(false));
} else {
  // Send a new friend request
  fetch(`${process.env.REACT_APP_API_PATH}/connections`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      toUserID: parseInt(effectiveUserId),
      fromUserID: parseInt(loggedInUserId),
      attributes: { type: "friend", status: "pending" },
    }),
  })
  .then((res) => res.json())
  .then((result) => {
    setConnectionStatus("pending");
    setConnectionId(result.id);
  })
  .catch((err) => {
    console.error("Error sending friend request:", err);
  })
  .finally(() => setConnectionLoading(false));
}
};

// If profileData is not yet loaded, display a loading message.
if (!profileData) {
  return (
    <div className="profile-page">
      <div className="profile-card">
        <p>{t("loading_profile")}</p>
      </div>
    </div>
  );
}

// Calculate post and comment counts.
const numericUserId = parseInt(effectiveUserId, 10);
const userPosts = allPosts.filter(
(post) =>
post.parentID === null &&
parseInt(post.authorID, 10) === numericUserId
);
const userComments = allPosts.filter(
  (post) =>
    post.parentID !== null &&
    parseInt(post.authorID, 10) === numericUserId
);

const postsCount = userPosts.length;
const commentsCount = userComments.length;

/* -------------------- Engagement Tiers Logic -------------------- */
const engagementTiers = [
{ name: "Rookie", posts: 2, comments: 3, icon: discus },
{ name: "Bronze", posts: 4, comments: 6, icon: discus },
{ name: "Silver", posts: 8, comments: 12, icon: discus },
{ name: "Gold", posts: 16, comments: 24, icon: discus },
{ name: "Platinum", posts: 32, comments: 48, icon: discus },
];
let highestTierIndex = -1;
for (let i = 0; i < engagementTiers.length; i++) {
const tier = engagementTiers[i];
if (postsCount >= tier.posts && commentsCount >= tier.comments) {
highestTierIndex = i;
} else {
break;
}
}
let currentEngagementBadge = null;
if (highestTierIndex >= 0) {
const completedTier = engagementTiers[highestTierIndex];
const { frameClass } = getTierClasses(completedTier.name);
currentEngagementBadge = {
name: completedTier.name + " Engagement",
tier: completedTier.name,
isCompleted: true,
icon: completedTier.icon,
frameClass,
};
}
let inProgressEngagementBadge = null;
if (highestTierIndex < engagementTiers.length - 1) {
const nextTier = engagementTiers[highestTierIndex + 1];
const { frameClass, fillClass } = getTierClasses(nextTier.name);
const neededPosts = Math.max(0, nextTier.posts - postsCount);
const neededComments = Math.max(0, nextTier.comments - commentsCount);
const postsProgress = Math.min(postsCount / nextTier.posts, 1);
const commentsProgress = Math.min(commentsCount / nextTier.comments, 1);
const overallProgress = Math.min(postsProgress, commentsProgress);
inProgressEngagementBadge = {
name: nextTier.name + " Engagement",
tier: nextTier.name,
icon: nextTier.icon,
isCompleted: false,
frameClass,
fillClass,
neededPosts,
neededComments,
overallProgress,
};
}

/* -------------------- Friends Badge Logic -------------------- */
const friendMilestones = [
{ tier: "Rookie", required: 1, name: "Friends Rookie", icon: Friend },
{ tier: "Bronze", required: 5, name: "Friends Bronze", icon: Friend },
{ tier: "Silver", required: 10, name: "Friends Silver", icon: Friend },
{ tier: "Gold", required: 20, name: "Friends Gold", icon: Friend },
{ tier: "Platinum", required: 40, name: "Friends Platinum", icon: Friend },
];
const friendCount = friends.length;
let highestFriendMilestoneIndex = -1;
for (let i = 0; i < friendMilestones.length; i++) {
if (friendCount >= friendMilestones[i].required) {
highestFriendMilestoneIndex = i;
} else {
break;
}
}
let currentFriendBadge = null;
if (highestFriendMilestoneIndex >= 0) {
const completedMilestone = friendMilestones[highestFriendMilestoneIndex];
const { frameClass } = getTierClasses(completedMilestone.tier);
currentFriendBadge = {
...completedMilestone,
isCompleted: true,
frameClass,
};
}
let inProgressFriendBadge = null;
if (highestFriendMilestoneIndex < friendMilestones.length - 1) {
const nextMilestone = friendMilestones[highestFriendMilestoneIndex + 1];
const { frameClass, fillClass } = getTierClasses(nextMilestone.tier);
const needed = nextMilestone.required - friendCount;
const overallProgress = Math.min(friendCount / nextMilestone.required, 1);
inProgressFriendBadge = {
...nextMilestone,
isCompleted: false,
frameClass,
fillClass,
neededFriends: needed,
overallProgress,
};
}

/* -------------------- Static Badges Logic -------------------- */
const staticBadges = [
{
name: "Account Created",
tier: "Rookie",
requirement: "Sign up for an account",
isCompleted: !!token,
icon: AccountCreated,
},
{
name: "First Post",
tier: "Rookie",
requirement: "Create your first post",
isCompleted: postsCount > 0,
icon: firstPost,
},
{
name: "Add Bio",
tier: "Bronze",
requirement: "Add a bio to your profile",
isCompleted: (profileData?.bio || "").trim().length > 0,
icon: Bio,
},
{
name: "5 Favorite Games",
tier: "Silver",
requirement: "Add 5 favorite games",
isCompleted: (profileData?.favoriteGames?.length || 0) >= 5,
icon: favgame,
},
];
const earnedStatic = staticBadges.filter((b) => b.isCompleted);

// Combine static and dynamic badges.
let earnedBadgesAll = [...earnedStatic];
if (currentEngagementBadge) earnedBadgesAll.push(currentEngagementBadge);
if (currentFriendBadge) earnedBadgesAll.push(currentFriendBadge);

// Sort earned badges by tier (descending: Platinum > Gold > Silver > Bronze > Rookie).
const tierOrder = { Platinum: 5, Gold: 4, Silver: 3, Bronze: 2, Rookie: 1 };
earnedBadgesAll.sort((a, b) => (tierOrder[b.tier] || 0) - (tierOrder[a.tier] || 0));

/* -------------------- In-progress Badges Logic -------------------- */
const inProgressBadgesAll = [];
if (inProgressEngagementBadge) inProgressBadgesAll.push(inProgressEngagementBadge);
if (inProgressFriendBadge) inProgressBadgesAll.push(inProgressFriendBadge);
const totalInProgress = inProgressBadgesAll.length;
let aggregateProgress = 0;
if (totalInProgress > 0) {
aggregateProgress =
inProgressBadgesAll.reduce((sum, badge) => sum + badge.overallProgress, 0) /
totalInProgress;
}

/* -------------------- Save Profile (Bio) -------------------- */
const saveProfileChanges = async () => {
if (!effectiveUserId || !token) {
console.log("No session found, preventing update.");
return;
}
try {
await updateAttributes({
  bio: editedBio,
  picture: profileData.picture || DEFAULT_PROFILE_ICON,
});
window.dispatchEvent(new Event("profileUpdated"));
} catch (error) {
console.error("Error saving bio:", error);
}
setEditing(false);
};

/* -------------------- Popup Content -------------------- */
function renderMilestonePopupContent(badge) {
  if (badge.isCompleted) {
    return <div>{t("completed")}</div>;
  }
  if (badge.neededPosts != null && badge.neededComments != null) {
    return (
      <div style={{ lineHeight: "1.4" }}>
        Need <strong>{badge.neededPosts}</strong> more post(s)
        <br />
        Need <strong>{badge.neededComments}</strong> more comment(s)
      </div>
    );
  }
  if (badge.neededFriends != null) {
    return (
      <div style={{ lineHeight: "1.4" }}>
        Need <strong>{badge.neededFriends}</strong> more friend(s)
      </div>
    );
  }
  return <div>{badge.requirement || "In Progress..."}</div>;
}

/* -------------------- Render -------------------- */
return (
<div className="profile-page">
{/* Profile Card */}
<div className="profile-card">
  <div className="profile-name-edit">
    <div className="profile-image-container">
      <img
        src={profileData?.picture || DEFAULT_PROFILE_ICON}
        alt={t("profile_image_alt")}
        className="profile-image"
      />
    </div>
    <div className="profile-username">
      {profileData?.username || t("loading")}
    </div>
    {isOwnProfile && (
      <button
        className="edit-button"
        onClick={() => setEditing(!editing)}
      >
        {editing ? t("cancel") : t("edit")}
      </button>
    )}
    {!isOwnProfile && connectionStatus !== "blocked" && (
      <button
        className="add-friend-button"
        onClick={handleFriendRequest}
        disabled={connectionLoading}
      >
        {connectionStatus === "pending" ? t("Cancel Request") : t("Add Friend")}
      </button>
    )}
  </div>
  {isOwnProfile && editing ? (
    <div>
      <textarea
        className="bio-edit-textarea"
        value={editedBio}
        placeholder={t("bio_placeholder")}
        onChange={(e) => setEditedBio(e.target.value)}
      />
      <button className="bio-save-button" onClick={saveProfileChanges}>
        {t("save")}
      </button>
    </div>
  ) : (
    <p>{profileData?.bio || ""}</p>
  )}
  {profileData?.firstName && profileData?.lastName && (
    <p>
      {t("name")}: {profileData.firstName} {profileData.lastName}
    </p>
  )}
  <div className="profile-tabs">
    <button
      className={activeTab === "overview" ? "active-tab" : ""}
      onClick={() => setActiveTab("overview")}
    >
      {t("overview")}
    </button>
    <button
      className={activeTab === "posts" ? "active-tab" : ""}
      onClick={() => setActiveTab("posts")}
    >
      {t("posts")}
    </button>
  </div>
</div>

{/* Overview Section */}
{activeTab === "overview" && (
  <div className="overview-wrapper">
    <div className="overview-left">
      {/* Favorite Games */}
      <div className="Favorites-games-list">
        <div className="favorites-title-bar">
          <h3 className="favorites-title">{t("favorite_games")}</h3>
          {isOwnProfile && (
            <button
              className="edit-favorites-button"
              onClick={() => navigate("/ProfileGameSelect")}
            >
              {t("edit")}
            </button>
          )}
        </div>
        <div className="games-list">
          {profileData?.favoriteGames && profileData.favoriteGames.length > 0
            ? profileData.favoriteGames.map((gameId, index) => {
                const game = Object.values(gamesData).find(
                  (g) => g.id === gameId
                );
                return game ? (
                  <div className="game-frame" key={index}>
                    <img
                      src={game.carouselImage}
                      alt={game.name}
                      className="game-image"
                    />
                  </div>
                ) : (
                  <div className="game-frame" key={index} />
                );
              })
            : [...Array(5)].map((_, index) => (
                <div className="game-frame" key={index} />
              ))}
        </div>
      </div>
    </div>

    {/* Right Column: Friends & Badges */}
    <div className="overview-right">
      {/* Friends List */}
      <div className="friends-list">
        <h3 className="friends-title">{t("friends")}</h3>
        <div className="friends-container">
          {friendsLoading ? (
            <p>{t("loading_friends")}</p>
          ) : friendsError ? (
            <p>{t("error")}: {friendsError}</p>
          ) : friends.length === 0 ? (
            <p>{t("no_connections_found")}</p>
          ) : (
            friends.slice(0, 4).map((friend) => (
              <div
                className="friend-slot"
                key={friend.id}
                onClick={() => navigate(`/profile/${friend.id}`)}
              >
                <img
                  src={friend.img}
                  alt={friend.name}
                  className="friend-image"
                />
                <span className="friend-name">{friend.name}</span>
              </div>
            ))
          )}
        </div>
        {friends.length > 4 && (
          <button
            className="add-friend-button"
            onClick={() => navigate("/friends")}
          >
            {t("add_friend")}
          </button>
        )}
      </div>

      {/* Badges Section */}
      <div className="badges-section">
        <h3 className="badges-title">{t("badges")}</h3>
        <div className="badge-legend">
          <h4 className="legend-title">{t("badge_tiers")}</h4>
          <div className="legend-item">
            <span className="legend-swatch badge-frame-rookie"></span>
            <span className="legend-text">{t("rookie")}</span>
          </div>
          <div className="legend-item">
            <span className="legend-swatch badge-frame-bronze"></span>
            <span className="legend-text">{t("bronze")}</span>
          </div>
          <div className="legend-item">
            <span className="legend-swatch badge-frame-silver"></span>
            <span className="legend-text">{t("silver")}</span>
          </div>
          <div className="legend-item">
            <span className="legend-swatch badge-frame-gold"></span>
            <span className="legend-text">{t("gold")}</span>
          </div>
          <div className="legend-item">
            <span className="legend-swatch badge-frame-platinum"></span>
            <span className="legend-text">{t("platinum")}</span>
          </div>
        </div>

        {/* Earned Badges */}
        <h3 className="badges-subtitle">{t("earned")}</h3>
        <div className="earned-badges-section">
          <div className="rookie-slots-container">
            {earnedBadgesAll.slice(0, 3).map((badge, index) => {
              const { frameClass } = getTierClasses(badge.tier);
              return (
                <div
                  key={index}
                  className={`rookie-slot ${frameClass}`}
                  onClick={() =>
                    setOpenEarnedPopupIndex(
                      openEarnedPopupIndex === index ? null : index
                    )
                  }
                >
                  <div className="badge-image-container">
                    <img src={badge.icon} alt={badge.name} />
                  </div>
                  <div className="badge-description-outside">
                    {badge.name}
                  </div>
                  {openEarnedPopupIndex === index && (
                    <div className="milestone-popup">
                      <div className="milestone-popup-header">
                        {badge.name}
                        <button
                          className="popup-close"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenEarnedPopupIndex(null);
                          }}
                        >
                          ×
                        </button>
                      </div>
                      <div className="milestone-popup-content">
                        {renderMilestonePopupContent(badge)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {earnedBadgesAll.length > 3 && (
            <button
              className="more-badges-button"
              onClick={() => setShowBadgeModal(true)}
            >
              {t("more_badges")}
            </button>
          )}
        </div>

        {/* Aggregated In-Progress Rectangle */}
        {inProgressBadgesAll.length > 0 && (() => {
          const tierOrder = { Rookie: 0, Bronze: 1, Silver: 2, Gold: 3, Platinum: 4 };
          let aggregatedBadgeClass = { frameClass: "badge-frame-gold", fillClass: "fill-gold" };
          if (inProgressBadgesAll.length === 1) {
            aggregatedBadgeClass = getTierClasses(inProgressBadgesAll[0].tier);
          } else {
            const selectedBadge = inProgressBadgesAll.reduce((prev, curr) =>
              tierOrder[curr.tier] < tierOrder[prev.tier] ? curr : prev
            );
            aggregatedBadgeClass = getTierClasses(selectedBadge.tier);
          }
          return (
            <div className="progress-bar-wrapper" style={{ marginBottom: "15px" }}>
              <div className={`rookie-rectangle ${aggregatedBadgeClass.frameClass}`}>
                <div className="rookie-progress-text">
                  {`${t("in_progress")} - ${inProgressBadgesAll.length} Badge${
                    inProgressBadgesAll.length > 1 ? "s" : ""
                  }`}
                </div>
                <div className="rookie-progress-bar">
                  <div
                    className={`rookie-progress-fill ${aggregatedBadgeClass.fillClass}`}
                    style={{ width: `${(aggregateProgress * 100).toFixed(0)}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })()}

        {/* Individual In-Progress Badges */}
        <div className="rookie-slots-container">
          {inProgressBadgesAll.length === 0 ? (
            <p style={{ textAlign: "center" }}>{t("no_in_progress_badges")}</p>
          ) : (
            inProgressBadgesAll.map((badge, index) => {
              const { frameClass } = getTierClasses(badge.tier);
              return (
                <div
                  key={index}
                  className={`rookie-slot ${frameClass} disabled-badge`}
                  onClick={() =>
                    setOpenInProgressPopupIndex(
                      openInProgressPopupIndex === index ? null : index
                    )
                  }
                >
                  <div className="badge-image-container">
                    <img src={badge.icon} alt={badge.name} />
                  </div>
                  <div className="badge-description-outside">{badge.name}</div>
                  {openInProgressPopupIndex === index && (
                    <div className="milestone-popup">
                      <div className="milestone-popup-header">
                        {badge.name}
                        <button
                          className="popup-close"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenInProgressPopupIndex(null);
                          }}
                        >
                          ×
                        </button>
                      </div>
                      <div className="milestone-popup-content">
                        {renderMilestonePopupContent(badge)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  </div>
)}

{/* Posts Section */}
{activeTab === "posts" && (
  <div className="posts-layout">
    <div className="posts-left">
      {loading ? (
        <p>{t("loading_posts")}</p>
      ) : userPosts.length === 0 ? (
        <p>{t("no_posts_yet")}</p>
      ) : (
        userPosts.map((post) => {
          const lines = post.content ? post.content.split("\n") : [];
          const parsedTitle = lines[0] || t("untitled_post");
          const parsedBody = lines.slice(1).join("\n") || "";
          const timeAgo = post.created
            ? formatTimeAgo(post.created)
            : t("unknown_date");
          return (
            <div
              key={post.id}
              className="profile-post"
              onClick={() => navigate(`/post/${post.id}`)}
            >
              <div className="post-header">
                <h4>{parsedTitle}</h4>
                <small>{timeAgo}</small>
              </div>
              <p>{parsedBody}</p>
            </div>
          );
        })
      )}
    </div>
  </div>
)}

{/* All Earned Badges Modal */}
{showBadgeModal && (
  <div className="modal-overlay">
    <div className="modal-content">
      <h6>{t("all_earned_badges")}</h6>
      <div className="all-earned-wrapper">
        {earnedBadgesAll.map((badge, index) => {
          const { frameClass } = getTierClasses(badge.tier);
          return (
            <div
              key={index}
              className={`rookie-slot ${frameClass} inside-modal-slot`}
              onClick={() =>
                setOpenEarnedPopupIndex(
                  openEarnedPopupIndex === index ? null : index
                )
              }
            >
              <div className="badge-image-container">
                <img src={badge.icon} alt={badge.name} />
              </div>
              <div className="badge-description-outside">{badge.name}</div>
              {openEarnedPopupIndex === index && (
                <div className="milestone-popup modal-popup">
                  <div className="milestone-popup-header">
                    {badge.name}
                    <button
                      className="popup-close"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenEarnedPopupIndex(null);
                      }}
                    >
                      ×
                    </button>
                  </div>
                  <div className="milestone-popup-content">
                    {renderMilestonePopupContent(badge)}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <button
        className="more-badges-button"
        onClick={() => setShowBadgeModal(false)}
      >
        {t("close")}
      </button>
    </div>
  </div>
)}
</div>
);
};

export default Profile;
